"use client";

import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@radix-ui/themes';
import { ExternalLink, Heart, Instagram, MessageCircle, Trash2, X } from 'lucide-react';
import InstagramStatusBadge from './InstagramStatusBadge';

export type InstagramPostDetails = {
  title?: string;
  imageUrl?: string | null;
  caption?: string | null;
  status?: string | null;
  publishedAt?: string | null;
  scheduledAt?: string | null;
  mediaId?: string | null;
  username?: string | null;
  permalink?: string | null;
  mediaType?: string | null;
  likeCount?: number | null;
  commentCount?: number | null;
  lastSyncAt?: string | null;
};

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function detail(label: string, value: string | number | null | undefined) {
  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900 break-all">{value ?? 'Not available'}</div>
    </div>
  );
}

export default function InstagramPostModal({
  post,
  open,
  onOpenChange,
  onDelete,
}: {
  post: InstagramPostDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
}) {
  if (!post) return null;
  const imageUrl = post.imageUrl || '';
  const permalink = post.permalink || '';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/45" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(96vw,620px)] max-h-[92vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-xl border border-gray-200 focus:outline-none">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 via-rose-500 to-amber-400 text-white">
                <Instagram className="h-5 w-5" />
              </div>
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900">Instagram Post Details</Dialog.Title>
                <Dialog.Description className="text-sm text-gray-500">
                  {post.title || 'Instagram post'}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          <div className="grid gap-5 p-5 md:grid-cols-[minmax(0,260px)_1fr]">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              {imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={imageUrl} alt="" className="aspect-square w-full object-cover" />
              ) : (
                <div className="aspect-square w-full flex items-center justify-center text-gray-400">
                  <Instagram className="h-12 w-12" />
                </div>
              )}
              <div className="border-t border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center gap-4 text-sm text-gray-700">
                  <span className="inline-flex items-center gap-1">
                    <Heart className="h-4 w-4 text-rose-500" />
                    {(post.likeCount ?? 0).toLocaleString()}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MessageCircle className="h-4 w-4 text-gray-500" />
                    {(post.commentCount ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <InstagramStatusBadge status={post.status} />
                <span className="text-xs text-gray-500">Last sync: {formatDateTime(post.lastSyncAt)}</span>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Caption</div>
                <p className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-800">
                  {post.caption || 'No caption'}
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {detail('Publish date & time', formatDateTime(post.publishedAt || post.scheduledAt))}
                {detail('Instagram Media ID', post.mediaId)}
                {detail('Username', post.username)}
                {detail('Media type', post.mediaType || 'IMAGE')}
                {detail('Permalink', permalink || null)}
                {detail('Status', post.status)}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
            <Button
              type="button"
              variant="soft"
              color="gray"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="soft"
              color="red"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
              Delete Post
            </Button>
            <a
              href={permalink || '#'}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white ${
                permalink
                  ? 'bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700'
                  : 'pointer-events-none bg-gray-300'
              }`}
            >
              <ExternalLink className="h-4 w-4" />
              Open on Instagram
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
